import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowRight, MapPin, Navigation } from 'lucide-react';
import appHome from '../../assets/shippers/apphome.png';
import InteractiveApp from './InteractiveApp';

const CTASection = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [showInteractiveApp, setShowInteractiveApp] = useState(false);
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, []);

    const handleImageLoad = useCallback(() => {
        setImageLoaded(true);
    }, []);

    const handleImageError = useCallback(() => {
        setImageError(true);
        console.error('Failed to load mobile app screenshot');
    }, []);

    return (
        <section
            ref={sectionRef}
            className="w-full bg-gradient-to-b from-gray-100 from-70% to-white to-50% py-10 overflow-hidden"
            aria-labelledby="ship-smarter-section-title"
            role="region"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                    {/* Left Content */}
                    <div className="space-y-8">
                        {/* Main heading */}
                        <div
                            className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                                }`}
                        >
                            <h2
                                id="ship-smarter-section-title"
                                className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 leading-tight"
                            >
                                Ship Smarter,<br />
                                <span className="text-[#F05A28]">Pay Fair</span>
                            </h2>
                        </div>

                        {/* Description */}
                        <div
                            className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                                }`}
                            style={{ transitionDelay: '0.2s' }}
                        >
                            <p className="text-lg text-gray-700 leading-relaxed max-w-xl">
                                Simplify your freight booking with ease! Fill out the form to get a competitive quote, choose
                                the right vehicle, and ensure a smooth, hassle-free shipment with real-time tracking and
                                transparent pricing.
                            </p>
                        </div>

                        {/* CTA Section with border accent */}
                        <div
                            className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                                }`}
                            style={{ transitionDelay: '0.4s' }}
                        >
                            <div className="flex items-start space-x-4">
                                {/* Orange border accent */}
                                <div className="w-1 h-16 bg-orange-500 rounded-full flex-shrink-0 mt-1"></div>

                                {/* CTA Button */}
                                <div>
                                    <button
                                        className="group bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 flex items-center space-x-3 focus:outline-none focus:ring-4 focus:ring-orange-500/30 shadow-lg hover:shadow-xl"
                                        aria-label="Get instant freight quote"
                                    >
                                        <span>Get an Instant Freight Quote Today!</span>
                                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Content - Interactive App */}
                    <div
                        className={`relative transform transition-all duration-1000 ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-[50px] opacity-0'
                            }`}
                        style={{ transitionDelay: '0.6s' }}
                    >
                        <div className="relative flex justify-center lg:justify-end">

                            <div className="relative">
                                {!imageError ? (
                                    <>
                                        {/* Loading skeleton */}
                                        {!imageLoaded && (
                                            <div className="absolute inset-0 bg-gray-300 rounded-[3rem] animate-pulse w-80 h-[600px]"></div>
                                        )}

                                        {/* Mobile Phone Frame */}
                                        <div className="relative">
                                            <img
                                                src={appHome}
                                                alt="RoadExpress mobile app showing freight booking interface with pickup and drop-off location selection on India map"
                                                className={`w-80 h-auto transition-opacity duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'
                                                    }`}
                                                onLoad={handleImageLoad}
                                                onError={handleImageError}
                                                loading="lazy"
                                                width="320"
                                                height="600"
                                            />
                                        </div>
                                    </>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Enhanced SEO Structured Data */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "SoftwareApplication",
                        "name": "RoadExpress Freight Booking",
                        "description": "Simplify freight booking with competitive quotes, vehicle selection, and real-time tracking with transparent pricing",
                        "applicationCategory": "BusinessApplication",
                        "operatingSystem": "iOS, Android, Web",
                        "offers": {
                            "@type": "Offer",
                            "description": "Instant freight quote service"
                        },
                        "featureList": [
                            "Competitive freight quotes",
                            "Vehicle selection",
                            "Real-time tracking",
                            "Transparent pricing",
                            "Hassle-free shipment"
                        ]
                    })
                }}
            />
        </section>
    );
};

export default CTASection;